import { GrElement } from "../core/base";
import type { BusPayload } from "../core/messages";
import { createPipeline, parseScale, type Transform } from "./transforms";

/**
 * <gr-link from="pad/note" to="matrix/frame" transform="noteToPixel"></gr-link>
 * <gr-link from="pot/raw" to="synth/pitch" scale="0 4095 100 1600" clamp></gr-link>
 * <gr-link from="ldr/lux" to="lamp/on" threshold="500" hysteresis="20"></gr-link>
 *
 * The Node-RED wire: subscribes to `from`, republishes on `to`. Invisible.
 *
 * `transform` names a global function `(payload, msg) => payload | undefined`;
 * returning undefined drops the message. Built-in transforms (see
 * ./transforms.ts) run after it: scale → smooth → threshold → throttle.
 */
export class GrLink extends GrElement {
  private pipeline: Transform = (payload) => payload;

  connectedCallback(): void {
    const source = this.publisherSource("gr-link");
    this.style.display = "none";
    const from = this.attr("from");
    const to = this.attr("to");
    if (!from || !to) {
      console.error("[granrojo] <gr-link> requires from and to attributes");
      return;
    }

    const scaleRaw = this.getAttribute("scale");
    const scale = parseScale(scaleRaw);
    if (scaleRaw && !scale) {
      console.error(`[granrojo] <gr-link> invalid scale "${scaleRaw}" — expected "inMin inMax outMin outMax"`);
    }
    this.pipeline = createPipeline({
      scale: scale ?? undefined,
      clamp: this.hasAttribute("clamp"),
      smooth: this.hasAttribute("smooth") ? this.numAttr("smooth", 0) : undefined,
      threshold: this.hasAttribute("threshold") ? this.numAttr("threshold", NaN) : undefined,
      hysteresis: this.hasAttribute("hysteresis") ? this.numAttr("hysteresis", 0) : undefined,
      throttle: this.hasAttribute("throttle") ? this.numAttr("throttle", 0) : undefined,
    });

    this.track(
      this.bus.subscribe(
        from,
        (msg) => {
          let payload: BusPayload | undefined = msg.payload;
          const transformName = this.attr("transform");
          if (transformName) {
            const fn = (globalThis as Record<string, unknown>)[transformName];
            if (typeof fn !== "function") {
              console.error(`[granrojo] <gr-link> transform "${transformName}" is not a global function`);
              return;
            }
            payload = (fn as (p: BusPayload, m: unknown) => BusPayload | undefined)(msg.payload, msg);
          }
          if (payload === undefined) return;
          payload = this.pipeline(payload);
          if (payload === undefined) return;
          this.bus.publish(to, payload, { src: source });
        },
        { replay: false },
      ),
    );
  }
}

customElements.define("gr-link", GrLink);
